import type {
  FieldMap,
  IntegratorFeedDictionary,
} from "../../../../interfaces/models/IIntegratorFirm";

const PERSON_COLLECTION_POINTERS = new Set(["practiceAreas", "offices"]);

const DROPPED_PERSON_DICTIONARY_KEYS = new Set([
  "cases",
  "lastChange",
  "externalId",
]);

const PERSON_DICTIONARY_ALIASES: Record<string, string> = {
  webUrl: "webpage",
};

export type FeedMapCollectionKey = "professional" | "office" | "practiceArea";

export type FeedMapCollectionPaths = Record<FeedMapCollectionKey, string>;

export type FeedMapSelections = {
  professional: FieldMap;
  office: FieldMap;
  practiceArea: FieldMap;
  collectionPaths: FeedMapCollectionPaths;
};

export function emptyFeedMapCollectionPaths(): FeedMapCollectionPaths {
  return { professional: "", office: "", practiceArea: "" };
}

export function emptyFeedMapSelections(): FeedMapSelections {
  return {
    professional: {},
    office: {},
    practiceArea: {},
    collectionPaths: emptyFeedMapCollectionPaths(),
  };
}

export function setCollectionPath(
  paths: FeedMapCollectionPaths,
  section: FeedMapCollectionKey,
  feedPath: string | null
): FeedMapCollectionPaths {
  return { ...paths, [section]: feedPath?.trim() ?? "" };
}

export function setFieldPath(
  map: FieldMap,
  fieldKey: string,
  feedPath: string | null
): FieldMap {
  const next = { ...map };
  const trimmed = feedPath?.trim() ?? "";
  if (!trimmed) {
    delete next[fieldKey];
    return next;
  }
  next[fieldKey] = trimmed;
  return next;
}

/** True when `feedPath` is a descendant of the collection node, not the node itself. */
export function isPathInsideCollection(
  feedPath: string,
  collectionPath: string
): boolean {
  const path = feedPath.trim();
  const collection = collectionPath.trim();
  return Boolean(collection) && path.startsWith(`${collection}.`);
}

export function keepPathsInsideCollection(
  map: FieldMap,
  collectionPath: string
): FieldMap {
  const kept: FieldMap = {};
  for (const [key, path] of Object.entries(map)) {
    if (isPathInsideCollection(path, collectionPath)) {
      kept[key] = path;
    }
  }
  return kept;
}

export function selectionsFromFeedDictionary(
  dictionary: IntegratorFeedDictionary | undefined
): FeedMapSelections {
  const selections = emptyFeedMapSelections();
  if (!dictionary) {
    return selections;
  }

  const personMap = dictionary.personCollection?.map ?? {};
  for (const [key, path] of Object.entries(personMap)) {
    if (!path || PERSON_COLLECTION_POINTERS.has(key) || DROPPED_PERSON_DICTIONARY_KEYS.has(key)) {
      continue;
    }
    const canonicalKey = PERSON_DICTIONARY_ALIASES[key] ?? key;
    selections.professional[canonicalKey] = path;
  }

  selections.office = compactFieldMap(dictionary.officeCollection?.map ?? {});
  delete selections.office.externalId;
  selections.collectionPaths.professional = (
    dictionary.personCollection?.path ?? ""
  ).trim();
  selections.collectionPaths.office = (
    dictionary.officeCollection?.path ?? personMap.offices ?? ""
  ).trim();

  const practiceAreaName = (
    dictionary.practiceAreaCollection?.map?.practiceAreas ??
    personMap.practiceAreas ??
    ""
  ).trim();
  if (practiceAreaName) {
    selections.practiceArea.practiceAreas = practiceAreaName;
  }
  const storedPracticeAreaCollection = (
    dictionary.practiceAreaCollection?.path ?? ""
  ).trim();
  selections.collectionPaths.practiceArea =
    storedPracticeAreaCollection ||
    (practiceAreaName ? collectionPathFromMappedPaths([practiceAreaName]) : "");
  return selections;
}

/**
 * Rebuilds the stored dictionary from the three grids.
 * Collection `path`s come from the selector under each entity title; if one is empty
 * the shared parent of that entity's mapped field paths is used, which is how older
 * configs were saved.
 */
export function feedDictionaryFromSelections(params: {
  selections: FeedMapSelections;
  existingFirmMap?: FieldMap;
}): IntegratorFeedDictionary | undefined {
  const professional = compactFieldMap(params.selections.professional);
  stripDroppedPersonDictionaryKeys(professional);
  const office = compactFieldMap(params.selections.office);
  delete office.externalId;
  const practiceAreaNamePath =
    params.selections.practiceArea.practiceAreas?.trim() || "";
  const practiceAreaCollectionPath =
    params.selections.collectionPaths.practiceArea.trim();

  const personMap: FieldMap = { ...professional };
  if (practiceAreaNamePath) {
    personMap.practiceAreas = practiceAreaNamePath;
  }

  const officePath =
    params.selections.collectionPaths.office.trim() ||
    collectionPathFromMappedPaths(Object.values(office));
  if (officePath) {
    personMap.offices = officePath;
  }

  const personPath =
    params.selections.collectionPaths.professional.trim() ||
    collectionPathFromMappedPaths(
      Object.values(professional).length > 0
        ? Object.values(professional)
        : Object.values(personMap)
    );

  const dictionary: IntegratorFeedDictionary = {};
  if (params.existingFirmMap && Object.keys(params.existingFirmMap).length > 0) {
    dictionary.firm = params.existingFirmMap;
  }

  if (personPath || Object.keys(personMap).length > 0) {
    dictionary.personCollection = { path: personPath, map: personMap };
  }

  if (Object.keys(office).length > 0) {
    dictionary.officeCollection = { path: officePath, map: office };
  }

  if (practiceAreaCollectionPath || practiceAreaNamePath) {
    const practiceAreaMap: FieldMap = {};
    if (practiceAreaNamePath) {
      practiceAreaMap.practiceAreas = practiceAreaNamePath;
    }
    dictionary.practiceAreaCollection = {
      path:
        practiceAreaCollectionPath ||
        collectionPathFromMappedPaths([practiceAreaNamePath]),
      map: practiceAreaMap,
    };
  }

  if (
    !dictionary.firm &&
    !dictionary.personCollection &&
    !dictionary.officeCollection &&
    !dictionary.practiceAreaCollection
  ) {
    return undefined;
  }
  return dictionary;
}

export function pathsFromFeedDictionary(
  dictionary: IntegratorFeedDictionary | undefined
): string[] {
  if (!dictionary) {
    return [];
  }

  const paths = [
    ...Object.values(dictionary.firm ?? {}),
    dictionary.personCollection?.path ?? "",
    ...Object.values(dictionary.personCollection?.map ?? {}),
    dictionary.officeCollection?.path ?? "",
    ...Object.values(dictionary.officeCollection?.map ?? {}),
    dictionary.practiceAreaCollection?.path ?? "",
    ...Object.values(dictionary.practiceAreaCollection?.map ?? {}),
  ];

  return [...new Set(paths.map((path) => path.trim()).filter(Boolean))];
}

function stripDroppedPersonDictionaryKeys(map: FieldMap): void {
  for (const key of DROPPED_PERSON_DICTIONARY_KEYS) {
    delete map[key];
  }
  delete map.webUrl;
}

function compactFieldMap(map: FieldMap): FieldMap {
  const compact: FieldMap = {};
  for (const [key, path] of Object.entries(map)) {
    const trimmed = path.trim();
    if (trimmed) {
      compact[key] = trimmed;
    }
  }
  return compact;
}

/**
 * Probe lists leaf paths only. Collection selectors need the repeating nodes above
 * those leaves (`Professionals.Professional` from `Professionals.Professional.FirstName`).
 */
export function ancestorAndSelfPaths(paths: string[]): string[] {
  const result = new Set<string>();
  for (const path of paths) {
    const parts = path.trim().split(".").filter(Boolean);
    for (let index = 1; index <= parts.length; index++) {
      result.add(parts.slice(0, index).join("."));
    }
  }
  return [...result].sort();
}

function collectionPathFromMappedPaths(paths: string[]): string {
  const nonempty = paths.map((path) => path.trim()).filter(Boolean);
  if (nonempty.length === 0) {
    return "";
  }

  const segments = nonempty.map((path) => path.split("."));
  const shortest = Math.min(...segments.map((parts) => parts.length));
  const common: string[] = [];

  for (let index = 0; index < shortest; index++) {
    const part = segments[0][index];
    if (!segments.every((parts) => parts[index] === part)) {
      break;
    }
    common.push(part);
  }

  // The shared prefix of the field paths is the repeating node, not a field itself.
  if (common.length === shortest) {
    common.pop();
  }
  return common.join(".");
}
